import { LitElement, css, html } from 'lit';
import { customElement, query, state } from 'lit/decorators';
import { isDefined, show_confetti } from '../helpers/utils';

import { getChallenePageByJoinCode } from '../helpers/api/challenge-api';
import { getQueryStringParameters } from '../helpers/urls';

const CODE_LENGTH = 6;

@customElement('join-page')
export class JoinPage extends LitElement {
    amplitude_callsite: string = 'Registration Page';

    @query('#joinCode')
    codeElement!: HTMLInputElement;

    @state()
    errorMessage?: string;

    @state()
    busy: boolean = false;

    async firstUpdated() {
        const queryString = getQueryStringParameters();
        const registrationKey = queryString.get('join');
        window.dispatchEvent(new CustomEvent('page-content-ready'));
        if (registrationKey) {
            await this.redirectToRegistration(registrationKey);
        }
    }

    async redirectToRegistration(initial_registration_key?: string) {
        const registration_key = (initial_registration_key ?? this.codeElement?.value ?? '').trim();
        if (!registration_key) {
            this.errorMessage = 'Enter your join code to get started.';
            return;
        }
        this.busy = true;
        try {
            const url = await getChallenePageByJoinCode(this.amplitude_callsite, registration_key.toUpperCase());
            this.errorMessage = undefined;
            if (isDefined(url)) {
                void show_confetti();
                window.location.href = url;
                return;
            }
            this.errorMessage = 'We couldn\'t find that one. Double-check the code and try again.';
        } catch (e) {
            this.errorMessage = 'We couldn\'t find that one. Double-check the code and try again.';
        }
        this.busy = false;
    }

    private onInput(e: Event) {
        const input = e.target as HTMLInputElement;
        input.value = input.value.toUpperCase().replace(/[^A-Z0-9-]/g, '');
        if (this.errorMessage) this.errorMessage = undefined;
    }

    private onKeydown(e: KeyboardEvent) {
        if (e.key === 'Enter') void this.redirectToRegistration();
    }

    render() {
        return html`
            <div class="wrapper">
                <div class="card">
                    <h1>You're Invited</h1>
                    <p class="lede">Enter the ${CODE_LENGTH}-letter code from the organizer and we'll take you straight there.</p>
                    <input
                        id="joinCode"
                        class="code"
                        type="text"
                        inputmode="latin"
                        autocapitalize="characters"
                        autocomplete="off"
                        autocorrect="off"
                        spellcheck="false"
                        maxlength="${CODE_LENGTH}"
                        placeholder="ABCDEF"
                        aria-label="Join code"
                        ?disabled="${this.busy}"
                        @input="${this.onInput}"
                        @keydown="${this.onKeydown}" />
                    <button
                        class="join"
                        ?disabled="${this.busy}"
                        @click="${async () => await this.redirectToRegistration()}">
                        ${this.busy ? 'Looking For It…' : 'Join'}
                    </button>
                    ${this.errorMessage ? html`<p class="error" role="alert">${this.errorMessage}</p>` : ''}
                </div>
                <p class="footnote">Don't have a code? Ask whoever invited you.</p>
            </div>
        `;
    }

    static get styles() {
        return css`
            :host {
                display: block;
            }

            .wrapper {
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                gap: 1.25rem;
                min-height: 100vh;
                padding: 1.5rem;
                background:
                    radial-gradient(120% 80% at 50% 0%, rgba(104, 157, 157, 0.35) 0%, rgba(16, 44, 61, 0) 60%),
                    linear-gradient(160deg, #102c3d 0%, #16394d 100%);
            }

            .card {
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
                width: 100%;
                max-width: 420px;
                padding: 2rem 1.75rem;
                border-radius: 16px;
                background-color: #ffffff;
                box-shadow: 0 18px 40px rgba(16, 44, 61, 0.28);
                text-align: center;
            }

            h1 {
                margin: 0;
                font-size: 1.5rem;
                line-height: 1.2;
                color: #1e1b2c;
            }

            .lede {
                margin: 0.5rem 0 1.5rem;
                font-size: 0.9rem;
                line-height: 1.45;
                color: #6b6779;
            }

            .code {
                box-sizing: border-box;
                width: 100%;
                padding: 0.85rem 0.5rem;
                border: 1px solid #d5d2dc;
                border-radius: 10px;
                background-color: #ffffff;
                font-family: inherit;
                font-size: 1.6rem;
                font-weight: 600;
                letter-spacing: 0.4em;
                text-align: center;
                text-indent: 0.4em;
                text-transform: uppercase;
                color: #1e1b2c;
            }

            .code::placeholder {
                color: #c9c5d2;
                font-weight: 500;
            }

            .code:focus {
                outline: none;
                border-color: #689d9d;
                box-shadow: 0 0 0 3px rgba(104, 157, 157, 0.25);
            }

            .code:disabled {
                background-color: #f6f5f8;
            }

            .join {
                margin-top: 1rem;
                padding: 0.85rem 1rem;
                border: none;
                border-radius: 10px;
                background-color: #102c3d;
                color: #ffffff;
                font-family: inherit;
                font-size: 1rem;
                font-weight: 600;
                cursor: pointer;
                transition: background-color 120ms ease;
            }

            .join:hover:not(:disabled) {
                background-color: #16394d;
            }

            .join:disabled {
                opacity: 0.6;
                cursor: default;
            }

            .error {
                margin: 1rem 0 0;
                font-size: 0.85rem;
                line-height: 1.4;
                color: var(--main-invalid-color, #d64550);
            }

            .footnote {
                margin: 0;
                font-size: 0.8rem;
                color: rgba(255, 255, 255, 0.7);
            }

            @media (max-width: 420px) {
                .card {
                    padding: 1.75rem 1.25rem;
                }

                .code {
                    font-size: 1.35rem;
                    letter-spacing: 0.3em;
                }
            }
        `;
    }
}
